
import axios from "axios";
import {Capacitor} from "@capacitor/core";
import * as Sentry from "@sentry/vue";
import {crashlytics} from "@/services/crashlytics";
import {useMainStore} from "@/store";
import ChannelProvider from "./channelProvider";

// A reconnect (fresh connect() or resuming an existing connector) must reach
// `connected` within this window, or ensureConnected() tears it down and tries
// again. Without this, a socket that never completes its handshake after the
// app resumes from background (stale network, hung TLS/DNS on wake) leaves the
// "Connecting…" overlay up forever with nothing to detect or retry it.
const RECONNECT_STALL_TIMEOUT_MS = 15000;

export default class EchoManager
{
    // Bumped on every connect() / connection.connect() attempt. Connection
    // event handlers close over the generation they were bound for and ignore
    // events once a newer attempt has superseded them — otherwise a late
    // `disconnected` from an old connector can null out a fresh one mid-handshake
    // (see onDisconnected) and strand the app with no further trigger to retry.
    static _generation = 0;
    static _reconnectWatchdog = null;

    static options()
    {
        return {
            broadcaster: 'reverb',
            key: import.meta.env.VITE_REVERB_APP_KEY,
            wsHost: import.meta.env.VITE_REVERB_HOST,
            wsPort: parseInt(import.meta.env.VITE_REVERB_PORT, 10),
            wssPort: parseInt(import.meta.env.VITE_REVERB_PORT, 10),
            forceTLS: import.meta.env.VITE_REVERB_SCHEME === 'https',
            enabledTransports: ['ws', 'wss'],
            // encrypted: true,
            // disableStats: true,
            authorizer: (channel) => {
                return {
                    authorize: (socketId, callback) => {
                        // Direct axios call (NOT the shared `request` service): broadcasting auth
                        // legitimately returns 403 for channels the user isn't entitled to, and
                        // `request` treats any 403 as a ban → redirects to the banned screen.
                        axios.post(
                            'https://' + import.meta.env.VITE_API_HOST + ':' + import.meta.env.VITE_API_PORT + '/broadcasting/auth',
                            {
                                socket_id: socketId,
                                channel_name: channel.name,
                            },
                            {
                                headers: {
                                    Authorization: 'Bearer ' + useMainStore().token,
                                    ...(Capacitor.isNativePlatform() && import.meta.env.VITE_NATIVE_CLIENT_KEY
                                        ? { 'X-Client-Key': import.meta.env.VITE_NATIVE_CLIENT_KEY }
                                        : {}),
                                },
                            },
                        )
                            .then(response => {
                                callback(false, response.data);
                            })
                            .catch(error => {
                                callback(true, error);
                            })
                    }
                }
            }
        }
    }

    static async connect()
    {
        if (window.echo) {
            return;
        }
        const generation = ++this._generation;
        this._armReconnectWatchdog(generation);
        const [{default: Echo}, {default: Pusher}] = await Promise.all([
            import("laravel-echo"),
            import("pusher-js"),
        ]);
        // Another attempt superseded this one while the dynamic imports were
        // resolving (e.g. a second foreground event, or a stale disconnect that
        // triggered its own reconnect). Don't wire up a connector nobody will use.
        if (generation !== this._generation) {
            return;
        }
        window.Pusher = Pusher;
        window.echo = new Echo(this.options());
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        window.echo.connector.pusher.connection.bind('state_change', function(states) {
            //
        });
        window.echo.connector.pusher.connection.bind('connecting', () => this.onConnecting(generation));
        // TODO: THIS COULD BE A PROMISE RESOLUTION
        window.echo.connector.pusher.connection.bind('connected', ({socket_id}) => this.onConnected(socket_id, generation));
        window.echo.connector.pusher.connection.bind('disconnected', () => this.onDisconnected(true, generation));
    }

    static onConnecting(generation = this._generation)
    {
        if (generation !== this._generation) {
            return;
        }
        useMainStore().SET_ECHO_CONNECTING();
    }

    static onConnected(socket_id, generation = this._generation)
    {
        if (generation !== this._generation) {
            return;
        }
        this._clearReconnectWatchdog();
        useMainStore().SET_ECHO_CONNECTED(socket_id);
        this.subscribeChannel('bracket');
        this.subscribeChannel('guild');
        this.subscribeChannel('system');
        this.subscribeChannel('user');
    }

    static subscribeChannel(name, retries = 2)
    {
        const channel = ChannelProvider.channels().find(c => c.name === name);
        const gate = typeof channel.gate === 'function' ? channel.gate() : channel.gate;
        if (useMainStore().connected && channel && gate) {
            const channelName = channel.name + this.getChannelSuffix(channel);
            const echoChannel = window.echo[channel.method](channelName);

            // For presence/private channels, listen for subscription errors and retry.
            // Auth can fail if membership hasn't fully propagated on the backend yet.
            if (retries > 0 && (channel.method === 'join' || channel.method === 'private')) {
                const pusherChannel = echoChannel.subscription;
                if (pusherChannel) {
                    const errorHandler = (status) => {
                        console.warn(`Channel "${channelName}" subscription failed (status: ${status}), retries left: ${retries}`);
                        pusherChannel.unbind('pusher:subscription_error', errorHandler);
                        window.echo.leave(channelName);
                        setTimeout(() => {
                            this.subscribeChannel(name, retries - 1);
                        }, 1000);
                    };
                    pusherChannel.bind('pusher:subscription_error', errorHandler);
                }
            }

            Object.keys(channel.messages).forEach(k => {
                echoChannel.listen(channel.messagePrefix + k, received => {
                    const handler = new channel.messages[k](received);
                    handler.handle();
                })
            });
            if (Object.prototype.hasOwnProperty.call(channel, 'notifications')) {
                echoChannel.notification((n) => {
                    if (Object.prototype.hasOwnProperty.call(channel.notifications, n.type)) {
                        const handler = new channel.notifications[n.type](n);
                        handler.add();
                    }
                });
            }
            if (Object.prototype.hasOwnProperty.call(channel, 'presence')) {
                if (Object.prototype.hasOwnProperty.call(channel.presence, 'here')) {
                    echoChannel.here(function (members) {
                        const handler = new channel.presence.here(members);
                        handler.handle();
                    });
                }
                if (Object.prototype.hasOwnProperty.call(channel.presence, 'joining')) {
                    echoChannel.joining(function (member) {
                        const handler = new channel.presence.joining(member);
                        handler.handle();
                    });
                }
                if (Object.prototype.hasOwnProperty.call(channel.presence, 'leaving')) {
                    echoChannel.leaving(function (member) {
                        const handler = new channel.presence.leaving(member);
                        handler.handle();
                    });
                }

                // Fallback: if the `here` callback was missed due to a race condition
                // (subscription completing before bindings), sync the members list from
                // the underlying Pusher channel after giving subscription time to settle.
                if (Object.prototype.hasOwnProperty.call(channel.presence, 'here')) {
                    const syncMembers = () => {
                        const pusherCh = echoChannel.subscription;
                        if (pusherCh && pusherCh.members && pusherCh.members.count > 0) {
                            const members = [];
                            pusherCh.members.each(member => members.push(member.info));
                            const handler = new channel.presence.here(members);
                            handler.handle();
                        }
                    };
                    // Also listen for subscription_succeeded directly on the Pusher channel
                    // as a secondary hook in case Echo's .here() binding was too late.
                    const pusherCh = echoChannel.subscription;
                    if (pusherCh) {
                        if (pusherCh.subscribed) {
                            // Channel already subscribed (cached), sync immediately.
                            syncMembers();
                        } else {
                            pusherCh.bind('pusher:subscription_succeeded', () => {
                                syncMembers();
                            });
                        }
                    }
                }
            }
        }
    }

    static getChannelSuffix(channel)
    {
        let suffix = '';
        if (Object.prototype.hasOwnProperty.call(channel, 'nameSuffix')) {
            const split = channel.nameSuffix.split('.');
            let stateEntry = useMainStore().$state;
            split.forEach(segment => {
                stateEntry = stateEntry[segment];
            });
            suffix = '.' + stateEntry;
        }
        return suffix;
    }

    static unsubscribeChannel(name)
    {
        const channel = ChannelProvider.channels().find(c => c.name === name);
        if (channel) {
            window.echo.leave(channel.name + this.getChannelSuffix(channel));
        }
    }

    static onDisconnected(reset = true, generation = this._generation)
    {
        // A late `disconnected` from a superseded connector (e.g. the old socket's
        // teardown event arriving after a newer reconnect attempt already started).
        // Acting on it here would null out the CURRENT window.echo/connector mid
        // handshake and strand the app with nothing left to trigger another retry.
        if (generation !== this._generation) {
            return;
        }
        this._clearReconnectWatchdog();
        // TODO: FORCE THIS FROM REMOTE END
        // connect() loads Echo asynchronously, so window.echo may not exist yet
        // (e.g. logging out immediately after login, before the import resolves).
        if (window.echo) {
            window.echo.disconnect();
            window.echo = null;
        }
        if (reset) {
            useMainStore().RESET_PROPERTY('echo');
        }
    }

    /**
     * Verify the websocket connection is live and reconnect if it has dropped.
     *
     * Intended to be called when the app regains focus (native foreground),
     * where mobile OSes may have silently killed the socket while backgrounded.
     * We inspect the real Pusher/Reverb connector state rather than the store
     * getter, because the store may be stale if a disconnect event never fired.
     */
    static ensureConnected()
    {
        // Nothing to maintain if the user isn't logged in — Echo is only
        // connected after references load post-authentication.
        if (! useMainStore().loggedIn) {
            return;
        }

        // No Echo instance yet: connect() is idempotent and safe to call.
        if (! window.echo) {
            this.connect().catch(error => console.error('Echo reconnection failed', error));
            return;
        }

        const connection = window.echo.connector?.pusher?.connection;
        const state = connection?.state;

        // Already connected or actively (re)connecting — leave it alone. A
        // reconnect already in flight has its own watchdog armed; connecting
        // does not get a fresh one here, so a repeated foreground event can't
        // keep pushing the deadline out indefinitely.
        if (state === 'connected' || state === 'connecting') {
            return;
        }

        // Socket has dropped (disconnected/unavailable/failed). Pusher can
        // resume an existing connector without a full teardown.
        if (connection && typeof connection.connect === 'function') {
            this._armReconnectWatchdog(this._generation);
            connection.connect();
            return;
        }

        // Fallback: tear the stale instance down and build a fresh one.
        // onDisconnected() nulls out window.echo so connect() won't early-return.
        this.onDisconnected();
        this.connect().catch(error => console.error('Echo reconnection failed', error));
    }

    /**
     * Arm a stall watchdog for the in-flight reconnect attempt identified by
     * `generation`. If `connected` isn't reached before the timeout, the stale
     * connector is torn down and a new attempt is started — bumping the
     * generation again, which also invalidates any late events from this one.
     */
    static _armReconnectWatchdog(generation)
    {
        this._clearReconnectWatchdog();
        this._reconnectWatchdog = window.setTimeout(() => {
            this._reconnectWatchdog = null;
            // Superseded or already connected by the time the timer fired —
            // nothing to do.
            if (generation !== this._generation || useMainStore().connected) {
                return;
            }

            const message = `Echo reconnect stalled for ${RECONNECT_STALL_TIMEOUT_MS}ms after foreground/resume; retrying`;
            crashlytics.recordException(message);
            Sentry.captureMessage(message, 'warning');

            // Force a full teardown and retry rather than resuming the same
            // wedged connector again. onDisconnected(true, generation) matches
            // the attempt we're abandoning so it isn't mistaken for a stale event
            // by a still-newer attempt, and bumps nothing itself — connect() does.
            this.onDisconnected(true, generation);
            this.connect().catch(error => console.error('Echo reconnection failed', error));
        }, RECONNECT_STALL_TIMEOUT_MS);
    }

    static _clearReconnectWatchdog()
    {
        if (this._reconnectWatchdog !== null) {
            window.clearTimeout(this._reconnectWatchdog);
            this._reconnectWatchdog = null;
        }
    }
}
